import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  createItem,
  updateItem,
  destroyItem,
  changeItemState,
  massUpdate,
  fetchItem,
  fetchSyncStatuses,
  fetchItems
} from '@/apps/traforama/api/creatives'
import { ICampaignsFilter } from '@/apps/traforama/interfaces/campaigns'
import { IAutoCpaSettingAttributes } from '@/api/types'
import useCrudStore from '@/hooks/useCrudStore'
import { ElMessage } from 'element-plus'

export const defaultAutoCpaSetting: IAutoCpaSettingAttributes = {
  total_budget: 0,
  hourly_budget: 0,
  desired_margin: 10,
  historical_data_days: 7,
  representative_dataset: 20000,
  recalculation_frequency: 12
}

export const useCreativesStore = defineStore('creatives', () => {
  const defaultFilter: ICampaignsFilter = {
    disabled: false,
    state: ['review', 'new', 'approved', 'paused']
  }

  const {
    currentItems,
    total,
    filter,
    selected,
    items,
    listLoading,
    itemsLoading,
    currentPage,
    error,
    itemById,
    payload,

    ToggleSelection,
    FetchItems,
    DestroyItem,
    SaveItem,
    SetItems,
    ClearSelected,
    SetFilters,
    SetCurrentPage,

    setItem,
    setCurrent
  } = useCrudStore({
    defaultFilter,
    fetchItems,
    fetchItem,
    createItem,
    updateItem,
    destroyItem
  })

  const autoCpaSettings = ref<{ [itemId: string]: IAutoCpaSettingAttributes }>(
    {}
  )
  const idsToClone = ref<string[]>([])

  const SetClonedItems = (ids: string[]) => {
    idsToClone.value = ids
  }

  const RemoveClonedItem = (id: string) => {
    idsToClone.value = idsToClone.value.filter((clonedId) => clonedId !== id)
  }

  const FetchItem = async (id: string) => {
    itemsLoading.value[id] = true
    error.value = null
    try {
      const response = await fetchItem(id)
      setCurrent(response.data)
      setItem(response.data)
      const autoCpaItem = response.included?.find(
        (item: any) => item.type === 'auto_cpa_setting'
      )
      if (autoCpaItem) {
        autoCpaSettings.value[String(autoCpaItem.attributes.creature_id)] =
          autoCpaItem.attributes
      }
      return response
    } catch (e: any) {
      error.value = e.toString()
    } finally {
      itemsLoading.value = {}
      error.value = null
    }
  }

  const ChangeState = async (id: string, event: string, reason?: string) => {
    itemsLoading.value[id] = true
    error.value = null
    try {
      await changeItemState(id, event, reason)
      await FetchItem(id)
    } catch (e: any) {
      error.value = e.toString()
      if (e.response.data.message) ElMessage.error(e.response.data.message)
    } finally {
      itemsLoading.value = {}
      error.value = null
    }
  }

  const MassUpdateItems = async (ids: string[], action: string, value: any) => {
    listLoading.value = true
    error.value = null
    try {
      await massUpdate(ids, action, value)
      for (const id of ids) {
        await FetchItem(id)
      }
    } catch (e: any) {
      error.value = e.toString()
      throw e
    } finally {
      itemsLoading.value = {}
      error.value = null
    }
  }

  const FetchSyncStatuses = async (ids: string[]) => {
    if (ids.length === 0) return {}

    const statuses = await fetchSyncStatuses(ids)
    Object.entries(statuses).forEach(([id, assetProcessed]) => {
      setItem({
        id,
        type: 'creature',
        attributes: { asset_processed: assetProcessed }
      })
    })
    return statuses
  }

  // Getters
  const autoCpaSettingById = (id: string): IAutoCpaSettingAttributes => {
    return autoCpaSettings.value[id] || defaultAutoCpaSetting
  }

  const getClonedItems = computed(() => idsToClone.value)

  return {
    currentItems,
    items,
    total,
    listLoading,
    itemsLoading,
    autoCpaSettings,
    idsToClone,
    FetchItems,
    FetchItem,
    SaveItem,
    DestroyItem,
    SetCurrentPage,
    SetFilters,
    ClearSelected,
    ToggleSelection,
    MassUpdateItems,
    FetchSyncStatuses,
    ChangeState,
    SetItems,
    SetClonedItems,
    RemoveClonedItem,
    selected,
    filter,
    defaultFilter,
    currentPage,
    itemById,
    autoCpaSettingById,
    getClonedItems
  }
})
